import React, { useEffect } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionCarousel.module.css';
import Slider from 'react-slick';
import '../../../../styles/slick/slick.css';
import '../../../../styles/slick/slick-theme.css';
import { Icons } from '../../../../components';
import { useIntl } from '../../../../util/reactIntl';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 290px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in a carousel.
 * The number blocks visible is defined by "numColumns" prop.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'carousel'} props.sectionType
 * @param {number?} props.numColumns
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionCarousel = props => {
  const intl = useIntl();
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns = 1,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    options,
    sectionName,
  } = props;
  const isBlog = sectionName === 'Blog';
  const isFeaturedCategories = sectionName === 'Featured Categories';
  const sliderContainerId = `${props.sectionId}-container`;
  const sliderId = `${props.sectionId}-slider`;
  const numberOfBlocks = blocks?.length;
  const hasBlocks = numberOfBlocks > 0;

  useEffect(() => {
    const setCarouselWidth = () => {
      if (hasBlocks) {
        const windowWidth = window.innerWidth;
        const elem = window.document.getElementById(sliderContainerId);
        const scrollbarWidth = window.innerWidth - document.body.clientWidth;
        const elementWidth =
          elem.clientWidth >= windowWidth - scrollbarWidth ? windowWidth : elem.clientWidth;
        const carouselWidth = elementWidth - scrollbarWidth;

        elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);
      }
    };
    setCarouselWidth();

    window.addEventListener('resize', setCarouselWidth);
    return () => window.removeEventListener('resize', setCarouselWidth);
  }, []);

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  const onSlideLeft = e => {
    var slider = window.document.getElementById(sliderId);
    const slideWidth = numColumns * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft - slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onSlideRight = e => {
    var slider = window.document.getElementById(sliderId);
    const slideWidth = numColumns * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft + slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideRight(e);
    }
  };
  const sliderSettings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    variableWidth: false,
    focusOnSelect: false,
    centerMode: false,
    responsive: [
      {
        breakpoint: 1128,
        settings: { slidesToShow: 4 },
      },
      {
        breakpoint: 991,
        settings: { slidesToShow: 3 },
      },
      {
        breakpoint: 767,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1 },
      },
    ],
  };
  if (isFeaturedCategories) {
    return (
      <div className={css.featuredCategoriesSec}>
        <SectionContainer
          id={sectionId}
          className={className}
          rootClassName={rootClassName}
          appearance={appearance}
          options={fieldOptions}
        >
          {hasHeaderFields ? (
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </header>
          ) : null}
          {hasBlocks ? (
            <div className={css.carouselContainer} id={sliderContainerId}>
              {/* <div
                className={classNames(css.carouselArrows, {
                  [css.notEnoughBlocks]: numberOfBlocks <= numColumns,
                })}
              >
                <button
                  className={css.carouselArrowPrev}
                  onClick={onSlideLeft}
                  onKeyDown={onKeyDown}
                >
                  ‹
                </button>
                <button
                  className={css.carouselArrowNext}
                  onClick={onSlideRight}
                  onKeyDown={onKeyDown}
                >
                  ›
                </button>
              </div> */}
              <div className={css.featuredCategoriesSlider} id={sliderId}>
                <Slider {...{ ...sliderSettings }}>
                  {blocks?.map((block, index) => {
                    const slideKey = block?.blockId || block?.blockName || `slide-${index}`;
                    return (
                      <div key={slideKey}>
                        <BlockBuilder
                          rootClassName={css.block}
                          ctaButtonClass={defaultClasses.ctaButton}
                          blocks={[block]}
                          sectionId={sectionId}
                          responsiveImageSizes={getResponsiveImageSizes(numColumns)}
                          options={options}
                          isFeaturedCategories={isFeaturedCategories}
                        />
                      </div>
                    );
                  })}
                </Slider>
              </div>
            </div>
          ) : null}
        </SectionContainer>
      </div>
    );
  }
  if (isBlog) {
    const featuredPostLabel = intl.formatMessage({
      id: 'SectionBlogs.featuredPost',
      defaultMessage: 'Featured Post',
    });
    const allPostsLabel = intl.formatMessage({
      id: 'SectionBlogs.allPosts',
      defaultMessage: 'All Posts',
    });
    return (
      <div className={css.blogSection}>
        <SectionContainer
          id={sectionId}
          className={className}
          rootClassName={rootClassName}
          appearance={appearance}
          options={fieldOptions}
        >
          {hasHeaderFields ? (
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field
                data={description}
                className={defaultClasses.description}
                options={fieldOptions}
              />
              <Field
                data={callToAction}
                className={defaultClasses.ctaButton}
                options={fieldOptions}
              />
            </header>
          ) : null}
          <div className={css.blogContentWrapper}>
            <div className={css.featuredPostWrapper}>
              <h3>{featuredPostLabel}</h3>
              <div className={css.featuredPost}>
                <BlockBuilder
                  rootClassName={css.block}
                  ctaButtonClass={defaultClasses.ctaButton}
                  blocks={blocks.slice(0, 1)}
                  sectionId={sectionId}
                  responsiveImageSizes={getResponsiveImageSizes(numColumns)}
                  options={options}
                />
              </div>
            </div>

            {hasBlocks ? (
              <div className={css.carouselContainer} id={sliderContainerId}>
                <div className={css.carouselHead}>
                  <h3>{allPostsLabel}</h3>
                  <div
                    className={classNames(css.carouselArrows, {
                      [css.notEnoughBlocks]: numberOfBlocks <= numColumns,
                    })}
                  >
                    <button
                      className={css.carouselArrowPrev}
                      onClick={onSlideLeft}
                      onKeyDown={onKeyDown}
                    >
                      <Icons name="leftAngle" />
                    </button>
                    <button
                      className={css.carouselArrowNext}
                      onClick={onSlideRight}
                      onKeyDown={onKeyDown}
                    >
                      <Icons name="rightAngle" />
                    </button>
                  </div>
                </div>
                <div className={getColumnCSS(numColumns)} id={sliderId}>
                  <BlockBuilder
                    rootClassName={css.block}
                    ctaButtonClass={defaultClasses.ctaButton}
                    blocks={blocks}
                    sectionId={sectionId}
                    responsiveImageSizes={getResponsiveImageSizes(numColumns)}
                    options={options}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </SectionContainer>
      </div>
    );
  }
  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks ? (
        <div className={css.carouselContainer} id={sliderContainerId}>
          <div
            className={classNames(css.carouselArrows, {
              [css.notEnoughBlocks]: numberOfBlocks <= numColumns,
            })}
          >
            <button className={css.carouselArrowPrev} onClick={onSlideLeft} onKeyDown={onKeyDown}>
              ‹
            </button>
            <button className={css.carouselArrowNext} onClick={onSlideRight} onKeyDown={onKeyDown}>
              ›
            </button>
          </div>
          <div className={getColumnCSS(numColumns)} id={sliderId}>
            <BlockBuilder
              rootClassName={css.block}
              ctaButtonClass={defaultClasses.ctaButton}
              blocks={blocks}
              sectionId={sectionId}
              responsiveImageSizes={getResponsiveImageSizes(numColumns)}
              options={options}
            />
          </div>
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionCarousel;
